import DataEditor, {
  EditableGridCell,
  GridCell,
  GridCellKind,
  GridColumn,
  Item,
  TextCell,
} from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { IValidationError } from '@wyz/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '~/common/context';
import { usePurchaseErrorTranslation } from '~/common/hooks';
import { PurchaseErrorCodeKeys } from '~/purchase/types';

interface EanErrorCell extends TextCell {
  errorMessage?: string;
}

export interface TableRow {
  ean?: string;
  quantity?: number;
}

type ExtendedGridColumn = GridColumn & {
  getContent(): GridCell;
};
interface EditableGridProps {
  onChange: (data: TableRow[]) => void;
  errors?: Array<IValidationError<PurchaseErrorCodeKeys>>;
}

export const EditableGrid: React.FC<EditableGridProps> = ({
  onChange,
  errors,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const [editedRows, setEditedRows] = useState<{
    errorsRef: typeof errors;
    rows: Set<number>;
  }>({ errorsRef: errors, rows: new Set() });

  const errorLookupByEan = useMemo(() => {
    const currentEditedRows =
      editedRows.errorsRef === errors ? editedRows.rows : new Set<number>();
    const lookup: Record<string, IValidationError<PurchaseErrorCodeKeys>> = {};
    errors?.forEach((error) => {
      const match = error.propertyPath.match(/requests\[(\d+)\]/);
      if (match) {
        const rowIndex = parseInt(match[1], 10);
        if (!currentEditedRows.has(rowIndex)) {
          lookup[error.propertyPath] = error;
        }
      }
    });
    return lookup;
  }, [errors, editedRows]);

  const [data, setData] = useState<TableRow[]>(
    new Array(20).fill({
      ean: '',
      quantity: undefined,
    }),
  );

  useEffect(() => {
    onChange(data);
  }, [data, onChange]);

  const columns = useMemo(
    (): ExtendedGridColumn[] => [
      {
        title: 'EAN',
        id: 'ean',
        grow: 1,
        getContent: () => ({
          kind: GridCellKind.Text,
          data: '',
          allowOverlay: true,
          displayData: '',
          readonly: false,
        }),
      },
      {
        title: 'Quantity',
        id: 'quantity',
        width: 250,
        hasMenu: false,
        getContent: () => ({
          kind: GridCellKind.Number,
          data: undefined,
          allowOverlay: true,
          displayData: '',
          readonly: false,
        }),
      },
    ],
    [],
  );

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell;
      const rowData = data[row];
      const columnId = columns[col]?.id as keyof TableRow;
      const columnContent = columns[col]?.getContent();

      if (!rowData) {
        return columnContent;
      }

      if (columnId === 'ean') {
        const value = rowData.ean || '';
        const error = errorLookupByEan[`requests[${row}].ean`];
        return {
          ...columnContent,
          kind: GridCellKind.Text,
          data: value,
          displayData: error
            ? `${value} > ${t(resolveErrorKey(error.errors[0]))}`
            : value,
          themeOverride: error
            ? {
                bgCell: '#fee',
                textDark: '#c00',
              }
            : undefined,
          contentAlign: 'left',
        } as EanErrorCell;
      } else {
        const error = errorLookupByEan[`requests[${row}].quantity`];
        const value =
          typeof rowData.quantity === 'number' ? rowData.quantity : undefined;
        return {
          ...columnContent,
          kind: GridCellKind.Number,
          data: value,
          displayData: error
            ? `${value ?? ''} > ${t(resolveErrorKey(error.errors[0]))}`
            : typeof rowData.quantity === 'number'
              ? (value?.toString() ?? '')
              : '',
          themeOverride: error
            ? {
                bgCell: '#fee',
                textDark: '#c00',
              }
            : undefined,
        };
      }
    },
    [data, columns, errorLookupByEan, t, resolveErrorKey],
  );

  const markRowAsEdited = useCallback(
    (row: number) => {
      setEditedRows((prev) => {
        const newRows = new Set(prev.errorsRef === errors ? prev.rows : []);
        newRows.add(row);
        return { errorsRef: errors, rows: newRows };
      });
    },
    [errors],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const columnId = columns[col]?.id as keyof TableRow;

      markRowAsEdited(row);

      setData((prevData) => {
        const newData = [...prevData];

        if (columnId === 'ean') {
          newData[row] = { ...newData[row], ean: newValue.data as string };
        } else if (columnId === 'quantity') {
          const numValue = Number(newValue.data);
          newData[row] = {
            ...newData[row],
            quantity: isNaN(numValue) ? undefined : numValue,
          };
        }

        return newData;
      });
    },
    [columns, markRowAsEdited],
  );

  const onPaste = useCallback(
    (target: Item, values: readonly (readonly string[])[]) => {
      const [startCol, startRow] = target;

      values.forEach((_, rowIndex) => {
        markRowAsEdited(startRow + rowIndex);
      });

      setData((prevData) => {
        const newData = [...prevData];

        values.forEach((row, rowIndex) => {
          const targetRow = startRow + rowIndex;

          row.forEach((value, colIndex) => {
            const targetCol = startCol + colIndex;
            const columnId = columns[targetCol]?.id;

            if (columnId === 'ean') {
              newData[targetRow] = { ...newData[targetRow], ean: value };
            } else if (columnId === 'quantity') {
              const numValue = Number(value);
              const quantity = isNaN(numValue) ? 0 : numValue;
              newData[targetRow] = {
                ...newData[targetRow],
                quantity,
              };
            }
          });
        });

        return newData;
      });

      return true;
    },
    [columns, markRowAsEdited],
  );

  const onRowAppended = useCallback(() => {
    setData((prevData) => [...prevData, { ean: '', quantity: undefined }]);
  }, []);

  return (
    <div style={{ height: '400px', width: '100%', position: 'relative' }}>
      <DataEditor
        getCellContent={getCellContent}
        columns={columns}
        rows={data.length}
        onCellEdited={onCellEdited}
        onPaste={onPaste}
        onRowAppended={onRowAppended}
        trailingRowOptions={{
          hint: t('app.theme.button.add'),
          sticky: true,
          tint: true,
        }}
        keybindings={{
          copy: true,
          paste: true,
          search: true,
          selectAll: true,
          delete: true,
        }}
        getCellsForSelection={true}
        rangeSelect='rect'
        columnSelect='single'
        rowSelect='multi'
        smoothScrollX={true}
        smoothScrollY={true}
        width='100%'
        height={400}
        rowMarkers='both'
        overscrollX={10}
        overscrollY={10}
      />
      <div id='portal' style={{ top: 0, left: 0, position: 'fixed' }}></div>
    </div>
  );
};

export default EditableGrid;
